import { useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Paperclip, X } from "lucide-react";
import { BUSINESS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Kitchen Remodeling",
  "Bathroom Remodeling",
  "New Home Construction",
  "Complete Home Renovation",
  "Luxury Custom Home",
  "Room Addition",
  "Interior Renovation",
  "Exterior Renovation",
  "Interior Design",
  "Other",
];

const BUDGETS = ["Under $50k", "$50k – $150k", "$150k – $300k", "$300k – $750k", "$750k – $1.5M", "Over $1.5M", "Prefer to discuss"];

const inputCls =
  "w-full bg-transparent border-b border-white/15 focus:border-goldw outline-none py-3 text-sm text-ivory placeholder:text-zinc3 transition-colors duration-300";
const labelCls = "block font-mono text-[9px] tracking-[0.25em] uppercase text-zinc3 mb-1";

export default function Contact() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    project_address: "", project_type: "", budget: "",
    start_date: "", description: "", contact_method: "Phone",
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Required";
    if (!form.last_name.trim()) e.last_name = "Required";
    if (!form.phone.trim()) e.phone = "Required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.project_type) e.project_type = "Select a project type";
    if (form.description.trim().length < 10) e.description = "Tell us a little about your project";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...chosen].slice(0, 5));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setStatus("sending");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/quotes`, fd);
      setStatus("success");
      toast.success("Your inquiry has been sent. We'll be in touch shortly.");
    } catch (err) {
      setStatus("idle");
      const msg = err.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(msg);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-32 lg:py-44 px-6 lg:px-24 bg-ink2">
      <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <p className="font-mono text-[10px] tracking-[0.35em] text-goldw uppercase mb-6">Chapter 07 — Contact</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.08]" data-testid="contact-headline">
            TRANSFORM YOUR HOME INTO A <span className="italic text-goldw">MASTERPIECE</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-zinc2 leading-relaxed max-w-md">
            Book a free, no-obligation consultation and tell us about the home you want to create.
          </p>

          <div className="mt-12 space-y-6" data-testid="contact-details">
            <div>
              <span className={labelCls}>Phone</span>
              <a href={BUSINESS.phoneHref} data-testid="contact-phone-link" className="font-serif text-2xl text-ivory hover:text-goldw transition-colors">
                {BUSINESS.phone}
              </a>
            </div>
            <div>
              <span className={labelCls}>Email</span>
              <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email-link" className="font-serif text-xl text-ivory hover:text-goldw transition-colors break-all">
                {BUSINESS.email}
              </a>
            </div>
            <div>
              <span className={labelCls}>Studio</span>
              <p className="text-sm text-zinc2 leading-relaxed">{BUSINESS.address}</p>
            </div>
            <div>
              <span className={labelCls}>Hours</span>
              <p className="text-sm text-zinc2">Monday–Friday 8 AM–6 PM<br />Saturday 9 AM–3 PM</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {status === "success" ? (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="quote-success"
              className="h-full min-h-[420px] border border-goldw/30 bg-card/40 flex flex-col items-center justify-center text-center px-8 py-16"
            >
              <CheckCircle2 className="text-goldw mb-6" size={44} strokeWidth={1.2} />
              <h3 className="font-serif font-light text-3xl text-ivory">Thank You.</h3>
              <p className="mt-4 text-sm text-zinc2 max-w-md leading-relaxed">
                Your project inquiry has been received. A member of the AMAA team will reach
                out within one business day to schedule your free consultation.
              </p>
              <button
                data-testid="quote-new-btn"
                onClick={() => { setStatus("idle"); setForm({ first_name: "", last_name: "", phone: "", email: "", project_address: "", project_type: "", budget: "", start_date: "", description: "", contact_method: "Phone" }); setFiles([]); }}
                className="mt-8 font-mono text-[10px] tracking-[0.25em] uppercase text-goldw border-b border-goldw/40 pb-1 hover:border-goldw transition-colors"
              >
                Submit another inquiry
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="border border-white/8 bg-card/40 p-8 lg:p-12" noValidate>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                <div>
                  <label htmlFor="qf-first" className={labelCls}>First Name *</label>
                  <input id="qf-first" data-testid="quote-first-name" className={inputCls} value={form.first_name} onChange={set("first_name")} autoComplete="given-name" />
                  {errors.first_name && <p className="mt-1 text-[11px] text-red-400" data-testid="error-first-name">{errors.first_name}</p>}
                </div>
                <div>
                  <label htmlFor="qf-last" className={labelCls}>Last Name *</label>
                  <input id="qf-last" data-testid="quote-last-name" className={inputCls} value={form.last_name} onChange={set("last_name")} autoComplete="family-name" />
                  {errors.last_name && <p className="mt-1 text-[11px] text-red-400" data-testid="error-last-name">{errors.last_name}</p>}
                </div>
                <div>
                  <label htmlFor="qf-phone" className={labelCls}>Phone *</label>
                  <input id="qf-phone" data-testid="quote-phone" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" />
                  {errors.phone && <p className="mt-1 text-[11px] text-red-400" data-testid="error-phone">{errors.phone}</p>}
                </div>
                <div>
                  <label htmlFor="qf-email" className={labelCls}>Email *</label>
                  <input id="qf-email" data-testid="quote-email" type="email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" />
                  {errors.email && <p className="mt-1 text-[11px] text-red-400" data-testid="error-email">{errors.email}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="qf-address" className={labelCls}>Project Address</label>
                  <input id="qf-address" data-testid="quote-address" className={inputCls} value={form.project_address} onChange={set("project_address")} placeholder="Street, City, TX" />
                </div>
                <div>
                  <label htmlFor="qf-type" className={labelCls}>Project Type *</label>
                  <select id="qf-type" data-testid="quote-project-type" className={`${inputCls} bg-ink2`} value={form.project_type} onChange={set("project_type")}>
                    <option value="">Select…</option>
                    {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {errors.project_type && <p className="mt-1 text-[11px] text-red-400" data-testid="error-project-type">{errors.project_type}</p>}
                </div>
                <div>
                  <label htmlFor="qf-budget" className={labelCls}>Estimated Budget</label>
                  <select id="qf-budget" data-testid="quote-budget" className={`${inputCls} bg-ink2`} value={form.budget} onChange={set("budget")}>
                    <option value="">Select…</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="qf-date" className={labelCls}>Desired Start Date</label>
                  <input id="qf-date" data-testid="quote-start-date" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.start_date} onChange={set("start_date")} />
                </div>
                <div>
                  <span className={labelCls}>Preferred Contact Method</span>
                  <div className="flex gap-6 pt-3">
                    {["Phone", "Email"].map((m) => (
                      <label key={m} className="flex items-center gap-2 cursor-pointer text-sm text-zinc2">
                        <input
                          type="radio"
                          name="contact_method"
                          data-testid={`quote-contact-${m.toLowerCase()}`}
                          checked={form.contact_method === m}
                          onChange={() => setForm({ ...form, contact_method: m })}
                          className="accent-[#C5A059]"
                        />
                        {m}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="qf-desc" className={labelCls}>Project Description *</label>
                  <textarea id="qf-desc" data-testid="quote-description" rows={4} className={`${inputCls} resize-none`} value={form.description} onChange={set("description")} placeholder="Tell us about your home and what you'd like to create…" />
                  {errors.description && <p className="mt-1 text-[11px] text-red-400" data-testid="error-description">{errors.description}</p>}
                </div>
                <div className="sm:col-span-2">
                  <span className={labelCls}>Inspiration Images, Plans or Measurements</span>
                  <button
                    type="button"
                    data-testid="quote-file-btn"
                    onClick={() => fileRef.current?.click()}
                    className="mt-2 inline-flex items-center gap-3 border border-dashed border-white/20 hover:border-goldw/60 px-5 py-4 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc2 hover:text-goldw transition-colors w-full justify-center"
                  >
                    <Paperclip size={14} /> Attach up to 5 files (images or PDF, 10MB each)
                  </button>
                  <input ref={fileRef} data-testid="quote-file-input" type="file" multiple accept=".jpg,.jpeg,.png,.webp,.heic,.pdf" className="hidden" onChange={onFiles} />
                  {files.length > 0 && (
                    <ul className="mt-3 space-y-1" data-testid="quote-file-list">
                      {files.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-center justify-between text-xs text-zinc2 bg-white/[0.03] px-3 py-2">
                          <span className="truncate max-w-[80%]">{f.name}</span>
                          <button type="button" data-testid={`quote-file-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`}>
                            <X size={14} className="text-zinc3 hover:text-red-400" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <button
                type="submit"
                data-testid="quote-submit-btn"
                disabled={status === "sending"}
                className="mt-10 w-full inline-flex items-center justify-center gap-3 bg-goldw text-ink font-mono text-[11px] tracking-[0.25em] uppercase px-8 py-5 hover:bg-ivory transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "sending" ? (
                  <><Loader2 size={16} className="animate-spin" /> Sending…</>
                ) : (
                  "Request My Free Consultation"
                )}
              </button>
              <p className="mt-4 text-center text-[11px] text-zinc3">
                No obligation. Your details stay with AMAA — never shared, never sold.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
